import React from 'react';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { DetailedRoomMetrics } from '../hooks/useMetrics';
import { Activity, Users, Radio, AlertCircle, Clock, TrendingUp } from 'lucide-react';

interface PerformanceDashboardProps {
  metrics: DetailedRoomMetrics | null;
  isLoading: boolean;
  error: Error | null;
}

export default function PerformanceDashboard({ metrics, isLoading, error }: PerformanceDashboardProps) {
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <AlertCircle size={24} />
          <div>
            <strong>Failed to load metrics</strong>
            <p>{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !metrics) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <Activity size={32} />
          <p>Loading performance metrics...</p>
        </div>
      </div>
    );
  }

  // Prepare data for charts
  const successFailureData = [
    { name: 'Success', value: metrics.translationStats.successRate * 100, color: '#48bb78' },
    { name: 'Failure', value: metrics.translationStats.failureRate * 100, color: '#f56565' },
  ];

  const languageConnectionData = Object.entries(metrics.connections.byLanguage).map(([lang, count]) => ({
    language: lang.toUpperCase(),
    connections: count,
  }));

  const latencyByLanguageData = Object.entries(metrics.translationStats.latencyByLanguage).map(([lang, latency]) => ({
    language: lang.toUpperCase(),
    latency: parseFloat((latency * 1000).toFixed(2)), // Convert to ms
  }));

  // Format time-series data for latency chart
  const latencyTimeSeriesData = metrics.timeSeries.map(point => ({
    time: new Date(point.timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }),
    latency: parseFloat((point.averageLatency / 1000).toFixed(2)), // Convert ms to seconds
    connections: point.activeConnections,
  }));

  // Format time-series data for throughput chart
  const throughputTimeSeriesData = metrics.timeSeries.map(point => ({
    time: new Date(point.timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }),
    translations: point.translationCount,
    chunks: point.audioChunksProcessed,
  }));

  const formatUptime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Performance Dashboard</h2>
        <div style={styles.timestamp}>
          Last updated: {new Date(metrics.system.lastUpdate).toLocaleTimeString()}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div style={styles.metricsGrid}>
        <MetricCard
          icon={<TrendingUp size={24} color="#667eea" />}
          label="Success Rate"
          value={`${(metrics.translationStats.successRate * 100).toFixed(1)}%`}
          color="#48bb78"
        />
        <MetricCard
          icon={<Users size={24} color="#667eea" />}
          label="Active Connections"
          value={metrics.connections.active.toString()}
          subtitle={`/ ${metrics.connections.total} total`}
        />
        <MetricCard
          icon={<Clock size={24} color="#667eea" />}
          label="Avg Latency"
          value={`${(metrics.translationStats.averageLatency * 1000).toFixed(0)}ms`}
        />
        <MetricCard
          icon={<Radio size={24} color="#667eea" />}
          label="Processing Rate"
          value={`${metrics.audio.processingRate.toFixed(2)}/s`}
          subtitle="chunks per second"
        />
        <MetricCard
          icon={<Activity size={24} color="#667eea" />}
          label="Total Translations"
          value={metrics.translationStats.totalTranslations.toString()}
        />
        <MetricCard
          icon={<AlertCircle size={24} color="#667eea" />}
          label="Error Count"
          value={metrics.errors.total.toString()}
          color={metrics.errors.total > 0 ? '#f56565' : '#48bb78'}
        />
      </div>

      {/* Charts Row 1: Latency & Success Rate */}
      <div style={styles.chartsRow}>
        {/* Latency Over Time */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Translation Latency (Last Hour)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={latencyTimeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" style={{ fontSize: '12px' }} />
              <YAxis label={{ value: 'Latency (s)', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="latency" stroke="#667eea" strokeWidth={2} name="Latency (s)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Success/Failure Rate */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Translation Success Rate</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={successFailureData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {successFailureData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2: Throughput & Connections */}
      <div style={styles.chartsRow}>
        {/* Throughput Over Time */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Translation Throughput</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={throughputTimeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" style={{ fontSize: '12px' }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="translations" stroke="#48bb78" strokeWidth={2} name="Translations" />
              <Line type="monotone" dataKey="chunks" stroke="#ed8936" strokeWidth={2} name="Audio Chunks" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Connections by Language */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Connections by Language</h3>
          <div style={styles.languageStats}>
            {languageConnectionData.map((item) => (
              <div key={item.language} style={styles.languageItem}>
                <span style={styles.languageLabel}>{item.language}</span>
                <span style={styles.languageValue}>{item.connections}</span>
                <div style={styles.languageBar}>
                  <div
                    style={{
                      ...styles.languageBarFill,
                      width: `${(item.connections / Math.max(...languageConnectionData.map((d) => d.connections), 1)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 3: Latency by Language & System Stats */}
      <div style={styles.chartsRow}>
        {/* Latency by Language */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Average Latency by Language</h3>
          <div style={styles.languageStats}>
            {latencyByLanguageData.map((item) => (
              <div key={item.language} style={styles.languageItem}>
                <span style={styles.languageLabel}>{item.language}</span>
                <span style={styles.languageValue}>{item.latency}ms</span>
                <div style={styles.languageBar}>
                  <div
                    style={{
                      ...styles.languageBarFill,
                      width: `${(item.latency / Math.max(...latencyByLanguageData.map((d) => d.latency), 1)) * 100}%`,
                      backgroundColor: item.latency < 1000 ? '#48bb78' : item.latency < 2000 ? '#ed8936' : '#f56565',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Stats */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>System Statistics</h3>
          <div style={styles.systemStats}>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>Uptime:</span>
              <span style={styles.statValue}>{formatUptime(metrics.system.uptime)}</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>Audio Buffer:</span>
              <span style={styles.statValue}>{metrics.audio.bufferSize} chunks</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>Chunks Received:</span>
              <span style={styles.statValue}>{metrics.audio.chunksReceived}</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>Chunks Processed:</span>
              <span style={styles.statValue}>{metrics.audio.chunksProcessed}</span>
            </div>
            <div style={styles.statRow}>
              <span style={styles.statLabel}>Processing Rate:</span>
              <span style={styles.statValue}>{metrics.audio.processingRate.toFixed(2)} chunks/s</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Errors */}
      {metrics.errors.recent.length > 0 && (
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Recent Errors</h3>
          <div style={styles.errorList}>
            {metrics.errors.recent.map((err, idx) => (
              <div key={idx} style={styles.errorItem}>
                <div style={styles.errorHeader}>
                  <span style={styles.errorCode}>{err.code}</span>
                  <span style={styles.errorTime}>{new Date(err.timestamp).toLocaleTimeString()}</span>
                </div>
                <div style={styles.errorMessage}>{err.message}</div>
                {err.severity && (
                  <span
                    style={{
                      ...styles.errorSeverity,
                      backgroundColor:
                        err.severity === 'critical' ? '#f56565' :
                        err.severity === 'high' ? '#ed8936' :
                        err.severity === 'medium' ? '#f6ad55' : '#a0aec0',
                    }}
                  >
                    {err.severity}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  color?: string;
}

function MetricCard({ icon, label, value, subtitle, color }: MetricCardProps) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricIcon}>{icon}</div>
      <div style={styles.metricContent}>
        <div style={styles.metricLabel}>{label}</div>
        <div style={{ ...styles.metricValue, color: color || '#2d3748' }}>{value}</div>
        {subtitle && <div style={styles.metricSubtitle}>{subtitle}</div>}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    backgroundColor: '#f7fafc',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1a202c',
    margin: 0,
  },
  timestamp: {
    fontSize: '14px',
    color: '#718096',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  metricIcon: {
    flexShrink: 0,
  },
  metricContent: {
    flex: 1,
  },
  metricLabel: {
    fontSize: '14px',
    color: '#718096',
    marginBottom: '4px',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: 'bold',
  },
  metricSubtitle: {
    fontSize: '12px',
    color: '#a0aec0',
    marginTop: '4px',
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  chartCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  chartTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginTop: 0,
    marginBottom: '20px',
  },
  languageStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '10px 0',
  },
  languageItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  languageLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#2d3748',
  },
  languageValue: {
    fontSize: '12px',
    color: '#718096',
  },
  languageBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  languageBarFill: {
    height: '100%',
    backgroundColor: '#667eea',
    transition: 'width 0.3s ease',
  },
  systemStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '10px 0',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
    borderBottom: '1px solid #e2e8f0',
  },
  statLabel: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: '500',
  },
  statValue: {
    fontSize: '16px',
    color: '#2d3748',
    fontWeight: '600',
  },
  errorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  errorItem: {
    padding: '12px',
    backgroundColor: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '8px',
  },
  errorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  errorCode: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#c53030',
  },
  errorTime: {
    fontSize: '12px',
    color: '#718096',
  },
  errorMessage: {
    fontSize: '13px',
    color: '#2d3748',
    marginBottom: '8px',
  },
  errorSeverity: {
    fontSize: '11px',
    padding: '4px 8px',
    borderRadius: '4px',
    color: 'white',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#718096',
    gap: '16px',
  },
  error: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '12px',
    color: '#c53030',
  },
};
